import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { DayTargetChip } from '@/modules/time-attendance/time-tracker/components/timesheet-week-view/timesheet-week-view';
import { DayTarget } from '@/modules/time-attendance/time-tracker/hooks/use-under-time';
import { startOfWeekDay } from '../../../utils/task-date';
import { TimeSheetRecord, TimeSheetTask } from '../../types/timesheet-manager.types';
import { formatHours } from '../timesheet-rollup-table/timesheet-rollup-table-columns';

/** One logged day: the tasks in scope (a chosen customer / project) and the day's status on all of them. */
export type ScopedDay = {
  day: string;
  record: TimeSheetRecord;
  tasks: TimeSheetTask[];
  target: DayTarget | null;
};

const billable = (tasks: readonly TimeSheetTask[]) =>
  tasks.reduce((sum, task) => sum + (Number(task.Billable) || 0), 0);
const nonBillable = (tasks: readonly TimeSheetTask[]) =>
  tasks.reduce((sum, task) => sum + (Number(task.NonBillable) || 0), 0);

const HEAD = 'h-9 px-2.5 text-[13px] font-medium text-muted-foreground';
const NUM = 'text-right tabular-nums';

/**
 * Daily entries grouped by week, Monday first (HR Module Prototype, Component Map › Employee timesheet
 * page): each week a small table of its days with the week's totals under it.
 */
export const TimesheetWeeks = ({ days }: { days: ScopedDay[] }) => {
  const { t, i18n } = useTranslation();
  const weeks = new Map<string, ScopedDay[]>();
  [...days]
    .sort((a, b) => b.day.localeCompare(a.day))
    .forEach((day) => {
      const week = startOfWeekDay(day.day);
      weeks.set(week, [...(weeks.get(week) ?? []), day]);
    });

  if (!days.length) {
    return <p className="py-6 text-sm text-muted-foreground">{t('TIMESHEET_MANAGER_NO_ENTRIES_FOUND')}</p>;
  }

  const weekday = (day: string) =>
    new Date(`${day}T00:00:00Z`).toLocaleDateString(i18n.language, {
      timeZone: 'UTC',
      weekday: 'short',
    });

  return (
    <div className="flex flex-col gap-6">
      {[...weeks.entries()].map(([week, weekDays]) => {
        const allTasks = weekDays.flatMap((day) => day.tasks);
        return (
          <section key={week} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">
              {t('TIMESHEET_MANAGER_WEEK_OF', { date: formatDateOnly(week) })}
            </h3>
            <Table>
              <TableHeader className="[&_tr]:border-b [&_tr]:border-input">
                <TableRow className="hover:bg-transparent">
                  <TableHead className={HEAD}>{t('DATE')}</TableHead>
                  <TableHead className={HEAD}>{t('PROJECTS')}</TableHead>
                  <TableHead className={`${HEAD} ${NUM}`}>{t('TIMESHEET_MANAGER_BILLABLE')}</TableHead>
                  <TableHead className={`${HEAD} ${NUM}`}>
                    {t('TIMESHEET_MANAGER_NON_BILLABLE')}
                  </TableHead>
                  <TableHead className={`${HEAD} ${NUM}`}>{t('TIMESHEET_MANAGER_TOTAL')}</TableHead>
                  <TableHead className={HEAD}>{t('STATUS')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {weekDays.map(({ day, tasks, target }) => (
                  <TableRow key={day} className="hover:bg-transparent">
                    <TableCell className="h-10 whitespace-nowrap px-2.5 py-1 tabular-nums">
                      <span className="mr-1.5 text-muted-foreground">{weekday(day)}</span>
                      {formatDateOnly(day)}
                    </TableCell>
                    <TableCell className="max-w-72 truncate px-2.5 py-1">
                      {tasks.map((task) => task.ProjectName ?? '—').join(', ')}
                    </TableCell>
                    <TableCell className={`px-2.5 py-1 ${NUM}`}>{formatHours(billable(tasks))}</TableCell>
                    <TableCell className={`px-2.5 py-1 ${NUM}`}>
                      {formatHours(nonBillable(tasks))}
                    </TableCell>
                    <TableCell className={`px-2.5 py-1 font-medium ${NUM}`}>
                      {formatHours(billable(tasks) + nonBillable(tasks))}
                    </TableCell>
                    <TableCell className="px-2.5 py-1">
                      <DayTargetChip target={target} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter className="border-t border-input bg-transparent font-semibold">
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-10 px-2.5 py-1" colSpan={2}>
                    {t('TIMESHEET_MANAGER_TOTAL')}
                  </TableCell>
                  <TableCell className={`px-2.5 py-1 ${NUM}`}>{formatHours(billable(allTasks))}</TableCell>
                  <TableCell className={`px-2.5 py-1 ${NUM}`}>
                    {formatHours(nonBillable(allTasks))}
                  </TableCell>
                  <TableCell className={`px-2.5 py-1 ${NUM}`}>
                    {formatHours(billable(allTasks) + nonBillable(allTasks))}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </section>
        );
      })}
    </div>
  );
};
